import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ClipboardCopy, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useLogSignatures,
  useResolveSignature,
  useMuteSignature,
  useErrorExportPrompt,
  type LogBucket,
} from "@/hooks/api";
import { cn } from "@/lib/cn";
import { RangePicker, parseRange } from "@/pages/monitoring/range-picker";
import { SummaryCards } from "./summary-cards";
import { SignatureTable } from "./signature-table";
import { SignatureDetail } from "./signature-detail";

const nf = new Intl.NumberFormat("nb-NO");

const BUCKETS: { key: LogBucket | "all"; label: string }[] = [
  { key: "action", label: "Krever handling" },
  { key: "auth", label: "Avvist · auth" },
  { key: "noise", label: "Skanning" },
  { key: "all", label: "Alle signaturer" },
];

export function ErrorOverview() {
  const [params, setParams] = useSearchParams();
  const range = parseRange(params.get("range"));
  const bucket = (params.get("bucket") as LogBucket | "all") || "action";
  const query = params.get("q") ?? "";

  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState(query);
  const searchRef = useRef<HTMLInputElement>(null);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const list = useLogSignatures({ range, bucket, q: query });
  const resolve = useResolveSignature();
  const mute = useMuteSignature();
  const exportPrompt = useErrorExportPrompt();

  const rows = list.data?.signatures ?? [];

  // Keep the selection valid as the list refetches, but never move it just
  // because counts changed — a list that jumps mid-triage is worse than a stale one.
  useEffect(() => {
    if (!rows.length) {
      setSelected(null);
      return;
    }
    if (!selected || !rows.some((r) => r.signature === selected)) {
      setSelected(rows[0]!.signature);
    }
  }, [rows, selected]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.signature === selected),
    [rows, selected]
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
        if (e.key === "Escape") el.blur();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const idx = rows.findIndex((r) => r.signature === selected);
      if (e.key === "j" && rows.length) {
        e.preventDefault();
        setSelected(rows[Math.min(rows.length - 1, idx + 1)]!.signature);
      } else if (e.key === "k" && rows.length) {
        e.preventDefault();
        setSelected(rows[Math.max(0, idx - 1)]!.signature);
      } else if (e.key === "e" && selected) {
        e.preventDefault();
        resolve.mutate({ signature: selected }, { onSuccess: () => toast.success("Markert som løst") });
      } else if (e.key === "m" && selected) {
        e.preventDefault();
        mute.mutate({ signature: selected, days: 7 }, { onSuccess: () => toast.success("Dempet i 7 dager") });
      } else if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, selected, resolve, mute]);

  async function runExport(mode: "copy" | "download") {
    try {
      const res = await exportPrompt.mutateAsync({ range, bucket });
      if (res.signatureCount === 0) {
        toast.info("Ingen uløste feil å eksportere");
        return;
      }
      if (mode === "copy") {
        await navigator.clipboard.writeText(res.prompt);
        toast.success(
          `${res.signatureCount} signaturer kopiert — lim inn i Claude Code`
        );
      } else {
        const blob = new Blob([res.prompt], { type: "text/markdown;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `feilrapport-${range}-${new Date().toISOString().slice(0, 10)}.md`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`${res.signatureCount} signaturer lastet ned`);
      }
      if (res.truncated) {
        toast.warning(
          `${res.droppedSignatures} signaturer utelatt for å holde rapporten kort`
        );
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Klarte ikke å lage rapporten");
    }
  }

  const counts = list.data?.counts;
  const totalEvents = list.data
    ? list.data.events.action + list.data.events.auth + list.data.events.noise
    : 0;

  const mutedCount = rows.filter(
    (r) => r.mutedUntil && new Date(r.mutedUntil) > new Date()
  ).length;

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex items-baseline gap-3.5 flex-wrap">
        <div className="text-2xl font-bold tracking-[-0.01em]">Feiloversikt</div>
        <div className="text-[13px] text-admin-text-subtle">
          {list.data
            ? `${counts!.all} signaturer fra ${nf.format(totalEvents)} hendelser`
            : "laster…"}
        </div>
        <div className="flex-1" />

        <button
          onClick={() => runExport("copy")}
          disabled={exportPrompt.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] bg-admin-primary hover:bg-admin-primary-hover text-admin-primary-fg text-xs font-semibold cursor-pointer disabled:opacity-60"
          title="Bygger en avkortet feilrapport du kan lime rett inn i Claude Code"
        >
          {exportPrompt.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ClipboardCopy className="h-3 w-3" />
          )}
          Kopier feilrapport
        </button>
        <button
          onClick={() => runExport("download")}
          disabled={exportPrompt.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] border border-admin-card-border text-admin-text-secondary text-xs cursor-pointer hover:bg-admin-card-hover disabled:opacity-60"
        >
          <Download className="h-3 w-3" />
          .md
        </button>

        <RangePicker value={range} onChange={(r) => setParam("range", r)} />
      </div>

      {list.data && <SummaryCards data={list.data} />}

      <div className="flex items-center gap-2 flex-wrap">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            onClick={() => setParam("bucket", b.key)}
            className={cn(
              "px-3.5 py-[7px] rounded-[7px] border text-[13px] font-medium cursor-pointer flex items-center gap-2 transition-colors duration-[120ms]",
              bucket === b.key
                ? "bg-admin-card-hover border-admin-slate text-admin-text-primary"
                : "bg-admin-card border-admin-card-border text-admin-text-secondary hover:bg-admin-card-hover/40"
            )}
          >
            <span>{b.label}</span>
            {/* Chip counts are unfiltered totals — what exists, not what is shown. */}
            <span className="font-mono text-[11px] opacity-60">
              {counts ? counts[b.key] : "—"}
            </span>
          </button>
        ))}

        <div className="flex-1" />

        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-[7px] border border-admin-card-border bg-admin-card w-[340px]">
          <input
            ref={searchRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setParam("q", draft || null);
            }}
            onBlur={() => setParam("q", draft || null)}
            placeholder="user:…, job:…, status:…, route:…"
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[13px] text-admin-text-primary placeholder:text-admin-text-subtle/70"
          />
          <span className="font-mono text-[11px] text-admin-text-subtle">/</span>
        </div>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-4 items-start">
        <SignatureTable
          rows={rows}
          selected={selected}
          onSelect={setSelected}
          loading={list.isLoading}
          hiddenLabel={
            mutedCount > 0
              ? `${mutedCount} dempet · ${nf.format(list.data?.events.noise ?? 0)} karantenehendelser skjult`
              : `${nf.format(list.data?.events.noise ?? 0)} karantenehendelser er holdt utenfor tellingen`
          }
          onShowAll={() => {
            // One update, not two: both setParam calls clone the same render's
            // params, so the second would overwrite the first and "Vis alt"
            // would clear the search while leaving the bucket filtered.
            const next = new URLSearchParams(params);
            next.set("bucket", "all");
            next.delete("q");
            setDraft("");
            setParams(next, { replace: true });
          }}
        />
        <SignatureDetail signature={selected} range={range} row={selectedRow} />
      </div>
    </div>
  );
}
